/**
 * UranIA PA — Form crea/modifica tipo documento (admin).
 *
 * Editor sezioni dinamico (add/remove/reorder), header label+titolo+icona,
 * scope core vs tenant. Riutilizzato da:
 *   - ``/admin/tipi-documento/new`` (mode=create)
 *   - ``/admin/tipi-documento/{id}`` (mode=edit)
 */

import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';

import {
  createTipoDocumento,
  TipiDocumentoApiError,
  updateTipoDocumento,
} from '../../lib/admin/tipi-documento-api.ts';
import type {
  TipoDocumentoDto,
  TipoDocumentoIcona,
  TipoDocumentoScope,
  TipoDocumentoSezioneDto,
  UpsertTipoDocumentoInput,
} from '../../lib/admin/tipi-documento-api.ts';

const TIPO_ID_PATTERN = /^[a-z][a-z0-9_]*$/u;
const ICONS: readonly TipoDocumentoIcona[] = [
  'base_giuridica',
  'avviso',
  'decreto_graduatoria',
  'decreto_finanziamento',
  'checklist_ricevibilita',
  'checklist_ammissibilita',
  'documento_generico',
];

type Mode = 'create' | 'edit';

export interface TipoDocumentoFormProps {
  readonly mode: Mode;
  readonly initial?: TipoDocumentoDto;
  readonly onSuccess?: (tipo: TipoDocumentoDto) => void;
}

export function TipoDocumentoForm(props: TipoDocumentoFormProps): JSX.Element {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const ids = {
    tipo_id: useId(),
    scope: useId(),
    label: useId(),
    titolo: useId(),
    icona: useId(),
    descrizione: useId(),
  };

  const isEdit = props.mode === 'edit';
  const initial = props.initial;

  const [tipoId, setTipoId] = useState(initial?.tipo_id ?? '');
  const [scope, setScope] = useState<TipoDocumentoScope>(
    initial !== undefined ? (initial.tenant_id === null ? 'core' : 'tenant') : 'tenant',
  );
  const [label, setLabel] = useState(initial?.label ?? '');
  const [titolo, setTitolo] = useState(initial?.titolo_docx ?? '');
  const [icona, setIcona] = useState<TipoDocumentoIcona>(
    initial?.icona ?? 'documento_generico',
  );
  const [descrizione, setDescrizione] = useState(initial?.descrizione ?? '');
  const [sezioni, setSezioni] = useState<readonly TipoDocumentoSezioneDto[]>(
    initial?.sezioni ?? [{ heading: '1. ', body: '[Inserisci...]' }],
  );

  const [validationError, setValidationError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const mutation = useMutation<
    TipoDocumentoDto,
    TipiDocumentoApiError,
    UpsertTipoDocumentoInput
  >({
    mutationFn: async (payload) =>
      isEdit
        ? updateTipoDocumento(payload.tipo_id, payload)
        : createTipoDocumento(payload),
    onSuccess: (data) => {
      setSuccessMessage(
        isEdit
          ? t('admin.tipi_documento.form.success_updated', { label: data.label })
          : t('admin.tipi_documento.form.success_created', { label: data.label }),
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'tipi-documento'] });
      void qc.invalidateQueries({ queryKey: ['tipi-documento'] });
      props.onSuccess?.(data);
    },
  });

  const addSezione = useCallback(() => {
    setSezioni((prev) => [
      ...prev,
      { heading: `${prev.length + 1}. Nuova sezione`, body: '[Inserisci contenuto...]' },
    ]);
  }, []);

  const removeSezione = useCallback((idx: number) => {
    setSezioni((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const moveSezione = useCallback((idx: number, direction: 1 | -1) => {
    setSezioni((prev) => {
      const next = [...prev];
      const target = idx + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target]!, next[idx]!];
      return next;
    });
  }, []);

  const updateSezione = useCallback(
    (idx: number, field: 'heading' | 'body', value: string) => {
      setSezioni((prev) =>
        prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)),
      );
    },
    [],
  );

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);
    setSuccessMessage(null);

    if (!TIPO_ID_PATTERN.test(tipoId)) {
      setValidationError(t('admin.tipi_documento.form.errors.invalid_tipo_id'));
      return;
    }
    if (label.trim().length === 0 || titolo.trim().length === 0) {
      setValidationError(t('admin.tipi_documento.form.errors.required_label_titolo'));
      return;
    }
    if (sezioni.length === 0) {
      setValidationError(t('admin.tipi_documento.form.errors.almeno_una_sezione'));
      return;
    }
    for (const sez of sezioni) {
      if (sez.heading.trim().length === 0 || sez.body.trim().length === 0) {
        setValidationError(t('admin.tipi_documento.form.errors.sezione_incompleta'));
        return;
      }
    }

    mutation.mutate({
      tipo_id: tipoId,
      scope,
      label: label.trim(),
      titolo_docx: titolo.trim(),
      icona,
      descrizione: descrizione.trim(),
      sezioni: sezioni.map((s) => ({
        heading: s.heading.trim(),
        body: s.body.trim(),
      })),
    });
  }

  const apiError = mutation.error;
  const isLocked = isEdit;  // tipo_id e scope non modificabili in edit

  return (
    <form onSubmit={handleSubmit} data-testid="admin-tipo-doc-form" noValidate>
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor={ids.tipo_id} className="form-label fw-semibold">
            {t('admin.tipi_documento.form.fields.tipo_id')}
          </label>
          <input
            id={ids.tipo_id}
            type="text"
            className="form-control font-monospace"
            value={tipoId}
            onChange={(e): void => setTipoId(e.target.value.toLowerCase())}
            placeholder="es. verbale_riunione"
            required
            disabled={isLocked || mutation.isPending}
            pattern="^[a-z][a-z0-9_]*$"
            data-testid="admin-tipo-doc-form-id"
          />
          <div className="form-text">{t('admin.tipi_documento.form.fields.tipo_id_hint')}</div>
        </div>
        <div className="col-md-3">
          <label htmlFor={ids.scope} className="form-label fw-semibold">
            {t('admin.tipi_documento.form.fields.scope')}
          </label>
          <select
            id={ids.scope}
            className="form-select"
            value={scope}
            onChange={(e): void => setScope(e.target.value as TipoDocumentoScope)}
            disabled={isLocked || mutation.isPending}
            data-testid="admin-tipo-doc-form-scope"
          >
            <option value="core">core (cross-tenant)</option>
            <option value="tenant">tenant (solo mio)</option>
          </select>
        </div>
        <div className="col-md-3">
          <label htmlFor={ids.icona} className="form-label fw-semibold">
            {t('admin.tipi_documento.form.fields.icona')}
          </label>
          <select
            id={ids.icona}
            className="form-select"
            value={icona}
            onChange={(e): void => setIcona(e.target.value as TipoDocumentoIcona)}
            disabled={mutation.isPending}
            data-testid="admin-tipo-doc-form-icona"
          >
            {ICONS.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-7">
          <label htmlFor={ids.label} className="form-label fw-semibold">
            {t('admin.tipi_documento.form.fields.label')}
          </label>
          <input
            id={ids.label}
            type="text"
            className="form-control"
            value={label}
            onChange={(e): void => setLabel(e.target.value)}
            placeholder="es. Verbale riunione AdG"
            required
            disabled={mutation.isPending}
          />
        </div>
        <div className="col-md-5">
          <label htmlFor={ids.titolo} className="form-label fw-semibold">
            {t('admin.tipi_documento.form.fields.titolo_docx')}
          </label>
          <input
            id={ids.titolo}
            type="text"
            className="form-control"
            value={titolo}
            onChange={(e): void => setTitolo(e.target.value)}
            placeholder="es. Verbale Comitato AdG"
            required
            disabled={mutation.isPending}
          />
        </div>
        <div className="col-12">
          <label htmlFor={ids.descrizione} className="form-label">
            {t('admin.tipi_documento.form.fields.descrizione')}
          </label>
          <input
            id={ids.descrizione}
            type="text"
            className="form-control"
            value={descrizione}
            onChange={(e): void => setDescrizione(e.target.value)}
            placeholder={t('admin.tipi_documento.form.fields.descrizione_placeholder')}
            disabled={mutation.isPending}
          />
        </div>
      </div>

      <hr className="my-4" />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="h6 mb-0">{t('admin.tipi_documento.form.sezioni_heading')}</h3>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={addSezione}
          disabled={mutation.isPending}
          data-testid="admin-tipo-doc-form-add-sezione"
        >
          + {t('admin.tipi_documento.form.add_sezione')}
        </button>
      </div>

      <div className="d-flex flex-column gap-3">
        {sezioni.map((sez, idx) => (
          <div
            key={idx}
            className="border rounded p-3"
            style={{ backgroundColor: '#f8f9fa' }}
            data-testid={`admin-tipo-doc-form-sezione-${idx}`}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="small fw-semibold text-muted">
                {t('admin.tipi_documento.form.sezione_label', { idx: idx + 1 })}
              </span>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={(): void => moveSezione(idx, -1)}
                  disabled={idx === 0 || mutation.isPending}
                  aria-label={t('admin.tipi_documento.form.move_up')}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={(): void => moveSezione(idx, 1)}
                  disabled={idx === sezioni.length - 1 || mutation.isPending}
                  aria-label={t('admin.tipi_documento.form.move_down')}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={(): void => removeSezione(idx)}
                  disabled={sezioni.length <= 1 || mutation.isPending}
                  aria-label={t('admin.tipi_documento.form.remove')}
                >
                  ×
                </button>
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label small mb-1">
                {t('admin.tipi_documento.form.sezione_heading')}
              </label>
              <input
                type="text"
                className="form-control form-control-sm"
                value={sez.heading}
                onChange={(e): void => updateSezione(idx, 'heading', e.target.value)}
                required
                disabled={mutation.isPending}
              />
            </div>
            <div>
              <label className="form-label small mb-1">
                {t('admin.tipi_documento.form.sezione_body')}
              </label>
              <textarea
                className="form-control form-control-sm font-monospace"
                rows={3}
                value={sez.body}
                onChange={(e): void => updateSezione(idx, 'body', e.target.value)}
                required
                disabled={mutation.isPending}
              />
            </div>
          </div>
        ))}
      </div>

      {validationError !== null && (
        <div className="alert alert-warning mt-3 mb-0" role="alert">
          {validationError}
        </div>
      )}
      {apiError !== null && apiError instanceof TipiDocumentoApiError && (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          <strong>{apiError.message}</strong>
          {apiError.detail !== null && (
            <p className="small mb-0 mt-1">{apiError.detail}</p>
          )}
        </div>
      )}
      {successMessage !== null && (
        <div className="alert alert-success mt-3 mb-0" role="status">
          {successMessage}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2 mt-3">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={mutation.isPending}
          data-testid="admin-tipo-doc-form-submit"
        >
          {mutation.isPending
            ? t('admin.tipi_documento.form.submitting')
            : isEdit
              ? t('admin.tipi_documento.form.submit_update')
              : t('admin.tipi_documento.form.submit_create')}
        </button>
      </div>
    </form>
  );
}
